import { usePermission } from "../hooks/usePermission";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../api/api";
import toast from "react-hot-toast";
import { Plus, X, Star, Filter, Pencil, Trash2, Calendar, Hash, User, BarChart2, Send, ExternalLink } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

/* ── constants ──────────────────────────────────────────────── */
const RATINGS = [
  { score: 5, label: "Excellent",    color: "text-green-600",  bg: "bg-green-50  border-green-200"  },
  { score: 4, label: "Good",         color: "text-blue-600",   bg: "bg-blue-50   border-blue-200"   },
  { score: 3, label: "Satisfactory", color: "text-yellow-600", bg: "bg-yellow-50 border-yellow-200" },
  { score: 2, label: "Needs Work",   color: "text-orange-600", bg: "bg-orange-50 border-orange-200" },
  { score: 1, label: "Poor",         color: "text-red-600",    bg: "bg-red-50    border-red-200"    },
];

const INTERACTION_TYPES = [
  { value: "TICKET",     label: "Ticket",     color: "bg-blue-100 text-blue-700"   },
  { value: "WHATSAPP",   label: "WhatsApp",   color: "bg-green-100 text-green-700" },
  { value: "CHAT",       label: "Chat",       color: "bg-purple-100 text-purple-700"},
  { value: "PHONE_CALL", label: "Phone Call", color: "bg-orange-100 text-orange-700"},
  { value: "OTHER",      label: "Other",      color: "bg-gray-100 text-gray-700"   },
];

const blankForm = {
  interactionType: "TICKET", customInteractionType: "",
  referenceNumber: "", referenceLink: "",
  ticketDate: "", responsiblePersonId: "",
  score: 0, notes: "",
  reviewText: "", internalComment: "", errorType: "", severity: "",
};

const ERROR_TYPES = ["Response Time", "Accuracy", "Communication", "Process", "Technical", "Other"];
const SEVERITIES = [
  { value: "low",    label: "Low",    color: "bg-blue-100 text-blue-700" },
  { value: "medium", label: "Medium", color: "bg-yellow-100 text-yellow-700" },
  { value: "high",   label: "High",   color: "bg-red-100 text-red-700" },
];

/* ── helpers ─────────────────────────────────────────────────── */
const getRating  = (score: number) => RATINGS.find(r => r.score === score);
const getIntType = (val: string)   => INTERACTION_TYPES.find(t => t.value === val);
const fmtDate    = (d: string)     => new Date(d).toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" });

const StarRating = ({ score }: { score: number }) => (
  <div className="flex gap-0.5">
    {[1,2,3,4,5].map(i => (
      <Star key={i} size={14} className={i <= score ? "text-yellow-400 fill-yellow-400" : "text-gray-200 fill-gray-200"}/>
    ))}
  </div>
);

/* ══════════════════════════════════════════════════════════════ */
const Quality: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const isManager = user?.role === "MANAGER";
  const canCreatePerm = usePermission("quality", "create");
  const canEditPerm   = usePermission("quality", "edit");
  const canDeletePerm = usePermission("quality", "delete");

  const [viewReview,  setViewReview]  = useState<any|null>(null);
  const [qComments,   setQComments]   = useState<any[]>([]);
  const [newQComment, setNewQComment] = useState("");

  const [reviews,   setReviews]   = useState<any[]>([]);
  const [stats,     setStats]     = useState<any[]>([]);
  const [users,     setUsers]     = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<"reviews"|"stats">("reviews");

  const [createOpen, setCreateOpen] = useState(false);
  const [editReview, setEditReview] = useState<any|null>(null);
  const [form,       setForm]       = useState({ ...blankForm });

  const [filters, setFilters] = useState({
    responsiblePersonId:"", referenceNumber:"",
    interactionType:"", from:"", to:"",
  });

  /* ── load ──────────────────────────────────────────────────── */
  const load = () => {
    const params: any = {};
    if (filters.responsiblePersonId) params.responsiblePersonId = filters.responsiblePersonId;
    if (filters.referenceNumber)     params.referenceNumber     = filters.referenceNumber;
    if (filters.interactionType)     params.interactionType     = filters.interactionType;
    if (filters.from)                params.from                = filters.from;
    if (filters.to)                  params.to                  = filters.to;
    api.get("/quality", { params }).then(r => setReviews(r.data));
    api.get("/quality/stats").then(r => setStats(r.data));
  };
  useEffect(() => {
    load();
    api.get("/users").then(r => setUsers(r.data)).catch(()=>{});
  }, []);

  /* ── Prefill from query param (e.g. /quality?ref=197674&type=TICKET) ── */
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const ref = searchParams.get("ref");
    const type = searchParams.get("type");
    if (ref) {
      const today = new Date().toISOString().slice(0, 10);
      setForm(f => ({
        ...f,
        referenceNumber: ref,
        interactionType: type || "TICKET",
        ticketDate: today,
      }));
      setCreateOpen(true);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  /* ── permissions ─────────────────────────────────────────────── */
  const canEdit   = (r: any) => canEditPerm;
  const canDelete = (r: any) => canDeletePerm;

  const openViewReview = async (r: any) => {
    setViewReview(r);
    try { const res = await api.get(`/quality/${r.id}/replies`); setQComments(res.data); }
    catch { setQComments(r.replies || []); }
  };

  const submitQComment = async () => {
    if (!newQComment.trim() || !viewReview) return;
    try {
      const res = await api.post(`/quality/${viewReview.id}/replies`, { content: newQComment });
      setQComments(prev => [...prev, res.data]);
      setNewQComment("");
    } catch { toast.error("Failed to add reply"); }
  };

  /* ── create ─────────────────────────────────────────────────── */
  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.reviewText?.trim()) { toast.error("Please fill review text"); return; }
    try {
      await api.post("/quality", { ...form, notes: form.reviewText || form.notes || "" });
      toast.success("Review submitted!");
      setCreateOpen(false); setForm({ ...blankForm }); load();
    } catch { toast.error("Failed to submit review"); }
  };

  /* ── edit ────────────────────────────────────────────────────── */
  const openEdit = (r: any) => {
    setEditReview(r);
    setForm({
      interactionType:       r.interactionType       || "TICKET",
      customInteractionType: r.customInteractionType || "",
      referenceNumber:       r.referenceNumber       || "",
      referenceLink:         r.referenceLink         || "",
      ticketDate:            r.ticketDate ? r.ticketDate.slice(0,10) : "",
      responsiblePersonId:   r.responsiblePerson?.id || "",
      score:                 r.score                 || 0,
      reviewText:            r.reviewText            || "",
      internalComment:       r.internalComment       || "",
      errorType:             r.errorType             || "",
      severity:              r.severity              || "",
      notes:                 r.notes                 || "",
    });
  };
  const submitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.reviewText?.trim()) { toast.error("Please fill review text"); return; }
    try {
      await api.put(`/quality/${editReview.id}`, form);
      toast.success("Review updated!");
      setEditReview(null); setForm({ ...blankForm }); load();
    } catch { toast.error("Failed to update review"); }
  };

  /* ── delete ─────────────────────────────────────────────────── */
  const deleteReview = async (r: any) => {
    if (!window.confirm(`Are you sure you want to delete this quality review?\n\nRef: #${r.referenceNumber} — ${getIntType(r.interactionType)?.label}`)) return;
    try {
      await api.delete(`/quality/${r.id}`);
      toast.success("Review deleted!"); load();
    } catch { toast.error("Failed to delete review"); }
  };

  /* ── review form JSX ─────────────────────────────────────────── */
  const reviewFormJSX = (onSubmit: (e:React.FormEvent)=>void, label:string, onClose:()=>void) => (
    <form onSubmit={onSubmit} className="p-5 space-y-4">
      {/* Interaction Type + Reference Number */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Interaction Type *</label>
          <select value={form.interactionType}
            onChange={e => setForm(f=>({...f, interactionType:e.target.value, customInteractionType:""}))} required
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500">
            {INTERACTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number *</label>
          <input value={form.referenceNumber}
            onChange={e=>setForm(f=>({...f, referenceNumber:e.target.value}))} required
            placeholder={form.interactionType==="TICKET"?"TKT-001":form.interactionType==="WHATSAPP"?"+201234...":"REF-001"}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
      </div>

      {/* Custom interaction type */}
      {form.interactionType === "OTHER" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Custom Interaction Type</label>
          <input value={form.customInteractionType}
            onChange={e=>setForm(f=>({...f, customInteractionType:e.target.value}))}
            placeholder="e.g. Telegram, Email, Social Media..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
      )}

      {/* Reference Link */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Reference Link <span className="text-gray-400 font-normal">(optional)</span></label>
        <input type="url" value={form.referenceLink}
          onChange={e=>setForm(f=>({...f, referenceLink:e.target.value}))}
          placeholder="https://..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
      </div>

      {/* Date + Person */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
          <input type="date" value={form.ticketDate}
            onChange={e=>setForm(f=>({...f, ticketDate:e.target.value}))} required
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Responsible Person *</label>
          <select value={form.responsiblePersonId}
            onChange={e=>setForm(f=>({...f, responsiblePersonId:e.target.value}))} required
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Select employee...</option>
            {users.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      </div>

      {/* Error Type + Severity */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Error Type</label>
          <select value={form.errorType} onChange={e => setForm(f => ({...f, errorType: e.target.value}))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Select...</option>
            {ERROR_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
          <div className="flex gap-2">
            {SEVERITIES.map(s => (
              <button key={s.value} type="button" onClick={() => setForm(f => ({...f, severity: s.value}))}
                className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-colors ${form.severity === s.value ? s.color + " border-current" : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Review Text */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Review Text *</label>
        <textarea value={form.reviewText}
          onChange={e => setForm(f => ({...f, reviewText: e.target.value}))} required rows={3}
          placeholder="Describe the quality of the interaction (visible to employee)..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 resize-none"/>
      </div>

      <div className="flex gap-3 pt-2 border-t border-gray-100">
        <button type="button" onClick={onClose}
          className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50">Cancel</button>
        <button type="submit"
          className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700">{label}</button>
      </div>
    </form>
  );

  /* ── render ─────────────────────────────────────────────────── */
  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Quality Reviews</h2>
        {canCreatePerm && (
          <button onClick={()=>{setCreateOpen(true);setForm({...blankForm});}}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium">
            <Plus size={15}/> New Review
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {(["reviews","stats"] as const).map(tab => (
          <button key={tab} onClick={()=>setActiveTab(tab)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab===tab ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-800"
            }`}>
            {tab==="reviews" ? <><Filter size={13}/> All Reviews</> : <><BarChart2 size={13}/> Performance Stats</>}
          </button>
        ))}
      </div>

      {/* ── Reviews Tab ───────────────────────────────────────── */}
      {activeTab === "reviews" && (
        <>
          {/* Filters */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Filter size={14}/> Filters</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <select value={filters.interactionType} onChange={e=>setFilters(f=>({...f,interactionType:e.target.value}))}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">All Types</option>
                {INTERACTION_TYPES.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <input value={filters.referenceNumber} onChange={e=>setFilters(f=>({...f,referenceNumber:e.target.value}))}
                placeholder="Reference #..."
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
              <select value={filters.responsiblePersonId} onChange={e=>setFilters(f=>({...f,responsiblePersonId:e.target.value}))}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">All Employees</option>
                {users.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <input type="date" value={filters.from} onChange={e=>setFilters(f=>({...f,from:e.target.value}))}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
              <input type="date" value={filters.to} onChange={e=>setFilters(f=>({...f,to:e.target.value}))}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={load}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 font-medium">
                Apply Filters
              </button>
              <button onClick={()=>{setFilters({responsiblePersonId:"",referenceNumber:"",interactionType:"",from:"",to:""});setTimeout(load,50);}}
                className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
                Reset
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto shadow-sm">
            <table className="w-full text-sm min-w-[800px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["Interaction Type","Reference #","Responsible Person","Details","Error Date","Actions"].map(h=>(
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reviews.map(r => {
                  const intType = getIntType(r.interactionType);
                  const rating  = getRating(r.score);
                  const displayType = r.interactionType === "OTHER" && r.customInteractionType
                    ? r.customInteractionType
                    : intType?.label || r.interactionType;
                  return (
                    <tr key={r.id} className="hover:bg-gray-50 transition-colors">

                      {/* Interaction Type */}
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${intType?.color || "bg-gray-100 text-gray-700"}`}>
                          {displayType}
                        </span>
                      </td>

                      {/* Reference # */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button onClick={() => openViewReview(r)} className="flex items-center gap-1 font-semibold text-blue-600 hover:underline">
                            <Hash size={12}/>{r.referenceNumber}
                          </button>
                          {r.referenceLink && (
                            <a
                              href={r.referenceLink.startsWith("http") ? r.referenceLink : `https://${r.referenceLink}`}
                              target="_blank" rel="noopener noreferrer"
                              onClick={e => e.stopPropagation()}
                              title={r.referenceLink}
                              className="text-gray-400 hover:text-blue-600 transition-colors"
                            >
                              <ExternalLink size={13}/>
                            </a>
                          )}
                        </div>
                      </td>

                      {/* Responsible Person */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                            {r.responsiblePerson?.name?.[0]}
                          </div>
                          <div>
                            <p className="font-medium text-gray-700 text-xs">{r.responsiblePerson?.name}</p>
                            {r.responsiblePerson?.department && (
                              <p className="text-xs text-gray-400">{r.responsiblePerson.department}</p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Details (error type + severity + repeat alerts) */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          {r.errorType && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{r.errorType}</span>}
                          {r.severity && <span className={`text-xs px-2 py-0.5 rounded-full ${r.severity === "high" ? "bg-red-100 text-red-700" : r.severity === "medium" ? "bg-yellow-100 text-yellow-700" : "bg-blue-100 text-blue-700"}`}>{r.severity}</span>}
                          {(r.errorRepeatCount || 0) >= 3 && <span className="text-xs px-2 py-0.5 rounded-full bg-red-500 text-white font-bold">Alert: {r.errorRepeatCount}x</span>}
                          {(r.errorRepeatCount || 0) === 2 && <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">Warning: 2x</span>}
                        </div>
                      </td>

                      {/* Error Date (ticketDate — when the error occurred, not when this row was created) */}
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1 text-gray-500 text-xs">
                          <Calendar size={11}/>{fmtDate(r.ticketDate)}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          {canEdit(r) && (
                            <button onClick={()=>openEdit(r)}
                              className="flex items-center gap-1 text-xs bg-gray-50 text-gray-600 border border-gray-200 px-2.5 py-1 rounded-lg hover:bg-gray-100 font-medium">
                              <Pencil size={11}/> Edit
                            </button>
                          )}
                          {canDelete(r) && (
                            <button onClick={()=>deleteReview(r)}
                              className="flex items-center gap-1 text-xs bg-red-50 text-red-500 border border-red-100 px-2.5 py-1 rounded-lg hover:bg-red-100 font-medium">
                              <Trash2 size={11}/> Delete
                            </button>
                          )}
                          {!canEdit(r) && !canDelete(r) && (
                            <span className="text-xs text-gray-400 italic">View only</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {reviews.length === 0 && (
              <div className="text-center py-14 text-gray-400">No reviews found</div>
            )}
          </div>
        </>
      )}

      {/* ── Stats Tab ─────────────────────────────────────────── */}
      {activeTab === "stats" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {stats.map((s: any) => (
            <div key={s.id} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-sm">
                  {s.name[0]}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-800 text-sm">{s.name}</p>
                  <p className="text-xs text-gray-500">{s.department || "No department"}</p>
                </div>
              </div>
              <div className="text-center py-3">
                <div className="text-3xl font-bold text-red-600">{s.totalErrors}</div>
                <div className="text-xs text-gray-500 uppercase tracking-wide mt-1">Total errors logged</div>
              </div>
            </div>
          ))}
          {stats.length === 0 && (
            <div className="col-span-3 text-center py-12 text-gray-400 bg-white rounded-xl border border-gray-200">
              No stats available
            </div>
          )}
        </div>
      )}

      {/* ══ Create Modal ══════════════════════════════════════════ */}
      {createOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg my-4">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">New Quality Review</h3>
                <p className="text-xs text-gray-500 mt-0.5">Evaluate customer interaction quality</p>
              </div>
              <button onClick={()=>setCreateOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
            </div>
            {reviewFormJSX(submitCreate, "Submit Review", ()=>setCreateOpen(false))}
          </div>
        </div>
      )}

      {/* ══ Edit Modal ════════════════════════════════════════════ */}
      {editReview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg my-4">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Edit Quality Review</h3>
                <p className="text-xs text-gray-500 mt-0.5">#{editReview.referenceNumber}</p>
              </div>
              <button onClick={()=>setEditReview(null)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
            </div>
            {reviewFormJSX(submitEdit, "Save Changes", ()=>setEditReview(null))}
          </div>
        </div>
      )}


      {/* View Review + Replies Modal */}
      {viewReview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg my-4 shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Review #{viewReview.referenceNumber}</h3>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {viewReview.errorType && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{viewReview.errorType}</span>}
                  {viewReview.severity && <span className={`text-xs px-2 py-0.5 rounded-full ${viewReview.severity === "high" ? "bg-red-100 text-red-700" : viewReview.severity === "medium" ? "bg-yellow-100 text-yellow-700" : "bg-blue-100 text-blue-700"}`}>{viewReview.severity}</span>}
                </div>
              </div>
              <button onClick={() => { setViewReview(null); setQComments([]); setNewQComment(""); }} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-xs text-gray-400">Employee</span><p className="text-gray-700">{viewReview.responsiblePerson?.name}</p></div>
                <div><span className="text-xs text-gray-400">Reviewer</span><p className="text-gray-700">{viewReview.reviewer?.name}</p></div>
                <div><span className="text-xs text-gray-400">Error Date</span><p className="text-gray-700">{fmtDate(viewReview.ticketDate)}</p></div>
              </div>
              {viewReview.referenceLink && (
                <div>
                  <span className="text-xs text-gray-400">Reference Link</span>
                  <a
                    href={viewReview.referenceLink.startsWith("http") ? viewReview.referenceLink : `https://${viewReview.referenceLink}`}
                    target="_blank" rel="noopener noreferrer"
                    className="mt-1 inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline break-all"
                  >
                    <ExternalLink size={14}/> {viewReview.referenceLink}
                  </a>
                </div>
              )}
              {viewReview.notes && <div className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">{viewReview.notes}</div>}
              {viewReview.reviewText && <div className="text-sm text-blue-700 bg-blue-50 rounded-lg px-3 py-2">{viewReview.reviewText}</div>}

              {/* Replies */}
              <div className="border-t border-gray-100 pt-3">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Replies ({qComments.length})</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {qComments.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No replies yet</p>}
                  {qComments.map((c: any) => (
                    <div key={c.id} className="bg-gray-50 rounded-lg px-3 py-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-gray-700">{c.author?.name}</span>
                        <span className="text-xs text-gray-400">{new Date(c.createdAt).toLocaleString("en-GB", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}</span>
                      </div>
                      <p className="text-sm text-gray-600">{c.content}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-3">
                  <input value={newQComment} onChange={e => setNewQComment(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitQComment(); } }}
                    placeholder="Add a reply..."
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
                  <button onClick={submitQComment} disabled={!newQComment.trim()}
                    className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-40 transition-colors">
                    <Send size={16}/>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Quality;
